/**
 * Modal: enter a written justification to acknowledge a QC FAIL.
 *
 * Minimum-length enforcement matches the backend service guard + DB CHECK
 * (`ACK_REASON_MIN_LEN = 20`). Submit stays disabled until the trimmed
 * reason hits that threshold.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

const ACK_REASON_MIN_LEN = 20

interface QcAcknowledgeDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (reason: string) => Promise<boolean>
  submitting: boolean
  error: string | null
}

export function QcAcknowledgeDialog({
  open,
  onClose,
  onSubmit,
  submitting,
  error,
}: QcAcknowledgeDialogProps): React.ReactElement | null {
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (open) setReason('')
  }, [open])

  if (!open) return null

  const trimmed = reason.trim()
  const remaining = Math.max(0, ACK_REASON_MIN_LEN - trimmed.length)
  const valid = trimmed.length >= ACK_REASON_MIN_LEN

  const handleSubmit = async (): Promise<void> => {
    if (!valid) return
    const ok = await onSubmit(trimmed)
    if (ok) onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl dark:bg-slate-800">
        <DialogHeader />
        <ReasonField
          reason={reason}
          remaining={remaining}
          valid={valid}
          error={error}
          onChange={setReason}
        />
        <DialogActions
          onClose={onClose}
          onSubmit={handleSubmit}
          valid={valid}
          submitting={submitting}
        />
      </div>
    </div>
  )
}

function DialogHeader(): React.ReactElement {
  const { t } = useTranslation('qc')
  return (
    <>
      <h3
        className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100"
        style={{ fontFamily: 'var(--font-display, Syne, sans-serif)' }}
      >
        {t('acknowledgeDialog.title')}
      </h3>
      <p className="mb-3 text-xs text-slate-600 dark:text-slate-400">
        {t('acknowledgeDialog.description', { min: ACK_REASON_MIN_LEN })}
      </p>
    </>
  )
}

interface ReasonFieldProps {
  reason: string
  remaining: number
  valid: boolean
  error: string | null
  onChange: (next: string) => void
}

function ReasonField(props: ReasonFieldProps): React.ReactElement {
  const { t } = useTranslation('qc')
  return (
    <>
      <label
        htmlFor="qc-ack-reason"
        className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300"
      >
        {t('acknowledgeDialog.reasonLabel')}
      </label>
      <textarea
        id="qc-ack-reason"
        value={props.reason}
        onChange={(e) => {
          props.onChange(e.target.value)
        }}
        rows={4}
        placeholder={t('acknowledgeDialog.reasonPlaceholder')}
        className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
      />
      {!props.valid && (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {t('acknowledgeDialog.minLengthHint', { remaining: props.remaining })}
        </p>
      )}
      {props.error !== null && (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400">{props.error}</p>
      )}
    </>
  )
}

interface DialogActionsProps {
  onClose: () => void
  onSubmit: () => Promise<void>
  valid: boolean
  submitting: boolean
}

function DialogActions(props: DialogActionsProps): React.ReactElement {
  const { t } = useTranslation('qc')
  return (
    <div className="mt-4 flex justify-end gap-2">
      <button
        type="button"
        onClick={props.onClose}
        disabled={props.submitting}
        className="rounded border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
      >
        {t('acknowledgeDialog.cancel')}
      </button>
      <button
        type="button"
        onClick={() => {
          void props.onSubmit()
        }}
        disabled={!props.valid || props.submitting}
        className="rounded bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-indigo-500 dark:hover:bg-indigo-400"
      >
        {props.submitting ? t('actions.acknowledging') : t('acknowledgeDialog.submit')}
      </button>
    </div>
  )
}
